import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldQuestion, Lock } from "lucide-react";
import { authApi } from "../../services/authApi";
import { usePermissions } from "../../hooks/usePermissions";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { ACCESS_PROFILES, profileDescriptionForRole } from "../../lib/accessProfiles";
import { useLocalization } from "../../hooks/useLocalization";

const CATEGORY_ORDER = ["Administration", "Operations", "Payroll", "Training & Compliance", "Store", "Staff"];

/** Read-only reference of the professional Access Profiles that map onto the underlying
 * granular roles. Shows how many current users hold each profile. Priority 4 redesign -
 * this page exists purely so a manager never has to reason about raw role codes. */
export default function AccessProfilesPage() {
  const { t } = useLocalization();
  const { canManageUsers } = usePermissions();
  const [counts, setCounts] = useState({});

  const CATEGORY_LABELS = {
    "Administration": t("mgmtAccessProfiles.categoryAdministration"),
    "Operations": t("mgmtAccessProfiles.categoryOperations"),
    "Payroll": t("mgmtAccessProfiles.categoryPayroll"),
    "Training & Compliance": t("mgmtAccessProfiles.categoryTrainingCompliance"),
    "Store": t("mgmtAccessProfiles.categoryStore"),
    "Staff": t("mgmtAccessProfiles.categoryStaff"),
  };

  useEffect(() => {
    if (!canManageUsers) return;
    authApi.listUsers().then((r) => {
      const tally = {};
      r.data.forEach((u) => { tally[u.role] = (tally[u.role] || 0) + 1; });
      setCounts(tally);
    }).catch(() => {});
  }, [canManageUsers]);

  return (
    <div className="max-w-5xl" data-testid="access-profiles-page">
      <PageHelpBanner pageKey="access-profiles" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtAccessProfiles.helpIntro")}</p>
        <p>{t("mgmtAccessProfiles.helpAssignPrefix")} <Link to="/management/security/users" className="underline font-semibold">{t("mgmtAccessProfiles.userAccounts")}</Link>{t("mgmtAccessProfiles.helpAssignSuffix")}</p>
      </PageHelpBanner>

      <h2 className="text-2xl font-bold tracking-tight text-gray-900 mb-1" data-testid="access-profiles-page-title">{t("mgmtAccessProfiles.title")}</h2>
      <p className="text-sm text-gray-500 mb-6">{t("mgmtAccessProfiles.subtitle")}</p>

      {CATEGORY_ORDER.map((category) => {
        const profiles = ACCESS_PROFILES.filter((p) => p.category === category);
        if (profiles.length === 0) return null;
        return (
          <div key={category} className="mb-8">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">{CATEGORY_LABELS[category] || category}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {profiles.map((p) => (
                <div key={p.role} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`access-profile-card-${p.role}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-sm font-bold text-slate-900">{t(`accessProfileLabel.${p.role}`)}</p>
                    <span className="text-xs font-semibold text-slate-500 bg-slate-50 px-2 py-0.5 rounded-full" data-testid={`access-profile-count-${p.role}`}>{counts[p.role] || 0} {counts[p.role] === 1 ? t("mgmtAccessProfiles.user") : t("mgmtAccessProfiles.users")}</span>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">{profileDescriptionForRole(p.role, t)}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 mt-6" data-testid="advanced-permissions-notice">
        <Lock size={16} className="text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-amber-900 flex items-center gap-1.5"><ShieldQuestion size={14} /> {t("mgmtAccessProfiles.advancedPermissions")}</p>
          <p className="text-xs text-amber-800 mt-1">{t("mgmtAccessProfiles.advancedPermissionsHelp")}</p>
        </div>
      </div>
    </div>
  );
}
